import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaStar, FaShoppingCart, FaHeart, FaChevronLeft, FaChevronRight, FaTimes } from 'react-icons/fa';
import mainService from '../services/mainService';
import config from '../services/config.json';
import ProductSlider from '../components/ProductSlider';
import Toast from '../components/Toast';
import useScrollToTop from '../hooks/useScrollToTop';
import { useToast } from '../contexts/ToastContext';
import { addToCart as addToCartUtil, triggerCartUpdate } from '../utils/cartUtils';
import './ProductDetails.css';

const ProductDetails = () => {
  const { showToast } = useToast();
  const { id, name } = useParams();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  // Customization states
  const [selectedColorId, setSelectedColorId] = useState(null);
  const [selectedSizeId, setSelectedSizeId] = useState(null);
  const [selectedZipType, setSelectedZipType] = useState('');
  const [zipPrice, setZipPrice] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedCustomizations, setSelectedCustomizations] = useState([]);
  const [isCustomizeModalOpen, setIsCustomizeModalOpen] = useState(false);
  const [actualPrice, setActualPrice] = useState(0);
  const [showAddToCartToast, setShowAddToCartToast] = useState(false);

  // Scroll to top when product changes
  useScrollToTop([id]);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Debug: Log product customizations changes
  useEffect(() => {
    if (product && product.ProductCustomizations) {
      console.log('Product customizations updated:', product.ProductCustomizations);
    }
  }, [product?.ProductCustomizations]);

  // Debug: Log product customizations changes
  useEffect(() => {
    console.log('Product customizations updated:', product?.ProductCustomizations);
  }, [product?.ProductCustomizations]);

  // Initialize cart function
  const initMyCart = () => {
    const existingCart = localStorage.getItem('shoppingCart');
    if (existingCart) {
      try {
        const cartItems = JSON.parse(existingCart);
        return cartItems;
      } catch (error) {
        console.error('Error parsing cart data:', error);
        return [];
      }
    }
    return [];
  };

  const getCategoryLabel = (raw) => {
    if (!raw) return '—';
    if (typeof raw === 'string') return raw;
    if (typeof raw === 'object') return raw.Name || raw.Code || raw.Title || raw.name || '—';
    return '—';
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [prodRes, relatedRes] = await Promise.allSettled([
          mainService.getProductById(id),
          mainService.getSimilarProductsById(id)
        ]);

        if (prodRes.status === 'fulfilled' && prodRes.value) {
          const productData = prodRes.value;

          // Initialize ProductCustomizations with IsSelected property
          if (productData.ProductCustomizations) {
            productData.ProductCustomizations = productData.ProductCustomizations.map(customization => ({
              ...customization,
              IsSelected: false // Ensure all customizations start as unselected
            }));
          }

          setProduct(productData);
          setSelectedImageIndex(0);

          // Initialize customization states
          setSelectedColorId(productData.ColourId || null);
          setSelectedSizeId(productData.SizeId || null);
          setActualPrice(productData.OfferPrice || productData.Price || 0);
          setQuantity(productData.MinOrders || 1);
          setSelectedCustomizations([]);

          console.log('Product data loaded:', productData);
        } else {
          setError('Product not found.');
        }

        if (relatedRes.status === 'fulfilled' && relatedRes.value) {
          setRelatedProducts(Array.isArray(relatedRes.value) ? relatedRes.value : []);
        }
      } catch (err) {
        console.error('Error fetching product details:', err);
        setError('Failed to load product details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchData();
    else setError('Product ID is required.');
  }, [id]);

  // Build images list and filter out null/empty values; normalize to full URLs
  const toUrl = (p) => {
    if (!p || typeof p !== 'string') return null;
    return p.startsWith('http') ? p : (config.apiUrl + p);
  };
  const imagesRaw = product ? [product.Image1, product.Image2, product.Image3, product.Image4, product.Image5] : [];
  const images = imagesRaw.map(toUrl).filter(Boolean);
  const mainImage = images[selectedImageIndex] || 'https://via.placeholder.com/600x600/333/fff?text=Product+Image';

  // Lightbox keyboard navigation effect (must be declared before any early returns)
  useEffect(() => {
    if (!isLightboxOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') showPrev();
      if (e.key === 'ArrowRight') showNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isLightboxOpen, images.length]);

  // Customization functions
  const chooseColor = (color) => {
    setSelectedColorId(color.ColourId);
  };

  const chooseSize = (size) => {
    setSelectedSizeId(size.SizeId);
    if (size.Price > 0) {
      setActualPrice(size.Price);
    }
    updateCustomizations();
  };

  const chooseZip = (zipType) => {
    setSelectedZipType(zipType);
    let price = 0;
    if (zipType === 'Jacket With Zipper') {
      price = product.Subcategory?.ZipPrice || 0;
    } else if (zipType === 'Hoodie') {
      price = product.Subcategory?.HoodiePrice || 0;
    } else if (zipType === 'Sweatshirt') {
      price = product.Subcategory?.SweatshirtPrice || 0;
    }
    setZipPrice(price);
    updateCustomizations();
  };

  const updateCustomizations = () => {
    const customizations = product.ProductCustomizations?.filter(pc => pc.IsSelected) || [];
    setSelectedCustomizations(customizations);
  };

  // Update selected customizations when product customizations change
  useEffect(() => {
    if (product?.ProductCustomizations) {
      const selectedCustomizations = product.ProductCustomizations.filter(pc =>
        pc.IsSelected === true
      );
      setSelectedCustomizations(selectedCustomizations);
    }
  }, [product?.ProductCustomizations]);

  const minQty = () => {
    setQuantity(prev => Math.max(prev - 1, 1));
  };

  const addQty = () => {
    setQuantity(prev => prev + 1);
  };

  const getFinalPrice = () => {
    let finalPrice = actualPrice;

    // Add customization prices
    const customizationTotal = selectedCustomizations.reduce((total, custom) => total + (custom.Price || 0), 0);
    finalPrice += customizationTotal;

    // Add zip price
    finalPrice += zipPrice;

    return finalPrice;
  };

  const addToCart = (data, qty) => {
    let customizationvalidation = true;

    // Check if customizations are required and completed
    if (data.IsPhotoNeed || data.IsTextNeed) {
      if (data.SelectedCustomizations === undefined) {
        // customizationvalidation = false;
      } else {
        if (!(data.SelectedCustomizations.length > 0)) {
          // customizationvalidation = false;
        } else {
          data.SelectedCustomizations.forEach((e) => {
            if (e.TextValue === "" && e.PhotoValue === "") {
              customizationvalidation = false;
            }
          });
        }
      }
    }

    // Validation checks
    if (data.OutOfStock) {
      showToast('Sorry, this product is not in stock', 'error');
      return;
    } else if (customizationvalidation === false) {
      showToast('Please finish your customizations', 'info');
      return;
    } else if (!(selectedColorId > 0)) {
      showToast('Please choose a color', 'info');
      return;
    } else if (!(selectedSizeId > 0)) {
      showToast('Please choose a size', 'info');
      return;
    } else if (data.Subcategory?.IsTypeNeeded && selectedZipType === "") {
      showToast('Please choose a type', 'info');
      return;
    } else {
      if (qty <= 0) {
        showToast('Sorry, invalid quantity', 'error');
        return;
      } else if (qty < (data.MinOrders || 1)) {
        showToast(`Minimum orders required: ${data.MinOrders || 1}`, 'info');
        return;
      }

      const toCart = {
        Id: data.Id,
        Name: data.Name,
        NameAr: data.NameAr,
        Price: data.Price,
        OfferPrice: data.OfferPrice,
        Image: data.Image1,
        Quantity: qty,
        MinOrders: data.MinOrders,
        ColourId: selectedColorId,
        SizeId: selectedSizeId,
        ZipType: selectedZipType,
        SelectedCustomizations: selectedCustomizations,
        ColorName: data.ProductColors?.find(c => c.ColourId === selectedColorId)?.ColourName || '',
        ColorNameAr: data.ProductColors?.find(c => c.ColourId === selectedColorId)?.ColourNameAr || '',
        SizeName: data.ProductSizes?.find(s => s.SizeId === selectedSizeId)?.SizeName || '',
        SizeNameAr: data.ProductSizes?.find(s => s.SizeId === selectedSizeId)?.SizeNameAr || ''
      }

      // Prepare customizations object
      // const customizations = {
      //   selectedColorId: selectedColorId,
      //   selectedSizeId: selectedSizeId,
      //   selectedZipType: selectedZipType,
      //   selectedCustomizations: data.selectedCustomizations,
      //   finalPrice: getFinalPrice(),
      //   colorName: data.ProductColors?.find(c => c.ColourId === selectedColorId)?.ColourName || '',
      //   colorNameAr: data.ProductColors?.find(c => c.ColourId === selectedColorId)?.ColourNameAr || '',
      //   sizeName: data.ProductSizes?.find(s => s.SizeId === selectedSizeId)?.SizeName || '',
      //   sizeNameAr: data.ProductSizes?.find(s => s.SizeId === selectedSizeId)?.SizeNameAr || ''
      // };

      // Add to cart using utility function
      const success = addToCartUtil(toCart);

      if (success) {
        setShowAddToCartToast(true);
      }
    }
  };

  if (loading) {
    return (
      <div className="product-details-page">
        <div className="container">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading product details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="product-details-page">
        <div className="container">
          <div className="error-container">
            <p className="error-message">{error}</p>
            <button
              className="btn btn-primary"
              onClick={() => window.location.reload()}
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="product-details-page">
        <div className="container">
          <p className="no-product-found">No product details available.</p>
        </div>
      </div>
    );
  }

  const decodedName = decodeURIComponent(name || product.Name || '');
  const categoryLabel = getCategoryLabel(product.Category ?? product.Code ?? product.category);

  const openLightbox = (index) => {
    if (!images.length) return;
    setLightboxIndex(index);
    setIsLightboxOpen(true);
  };

  const closeLightbox = () => setIsLightboxOpen(false);
  const showPrev = (e) => { e?.stopPropagation?.(); if (images.length) setLightboxIndex((prev) => (prev - 1 + images.length) % images.length); };
  const showNext = (e) => { e?.stopPropagation?.(); if (images.length) setLightboxIndex((prev) => (prev + 1) % images.length); };



  return (
    <div className="product-details-page">
      <div className="container">
        <nav className="breadcrumbs">
          <Link to="/">Home</Link>
          <span className="breadcrumb-sep">/</span>
          <Link to="/products">Products</Link>
          <span className="breadcrumb-sep">/</span>
          <span className="current">{decodedName}</span>
        </nav>

        <div className="details-layout">
          <div className="details-media">
            <div className="details-image">
              <img
                src={mainImage}
                alt={decodedName}
                onClick={() => openLightbox(selectedImageIndex)}
                role="button"
              />
            </div>
            {images.length > 1 && (
              <div className="thumbs">
                {images.map((src, idx) => (
                  <div
                    key={idx}
                    className={`thumb${idx === selectedImageIndex ? ' selected' : ''}`}
                    onClick={() => setSelectedImageIndex(idx)}
                    onDoubleClick={() => openLightbox(idx)}
                    role="button"
                  >
                    <img src={src} alt={decodedName} />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="details-info">
            <h1 className="details-title">{decodedName}</h1>
            <p className="details-id">ID: {id}</p>

            {product.AvgRating > 0 && (
              <div className="product-rating">
                <div className="stars">
                  {[...Array(5)].map((_, i) => (
                    <FaStar
                      key={i}
                      className={i < Math.floor(product.AvgRating) ? 'star filled' : 'star'}
                    />
                  ))}
                </div>
                <span className="rating-text">({product.AvgRating})</span>
              </div>
            )}

            <div className="details-price">
              {product.OfferPrice && product.OfferPrice < product.Price ? (
                <>
                  <span className="price original-price">AED {product.Price}</span>
                  <span className="price offer-price">AED {getFinalPrice()}</span>
                </>
              ) : (
                <span className="price">AED {getFinalPrice()}</span>
              )}
              {(zipPrice > 0 || selectedCustomizations.length > 0) && (
                <div className="price-breakdown">
                  <small>Base: AED {actualPrice}</small>
                  {zipPrice > 0 && <small> + Type: AED {zipPrice}</small>}
                  {selectedCustomizations.length > 0 && (
                    <small> + Customizations: AED {selectedCustomizations.reduce((total, custom) => total + (custom.Price || 0), 0)}</small>
                  )}
                </div>
              )}
            </div>

            {/* Color Selection */}
            {product.IsColorSelection && product.ProductColors && product.ProductColors.length > 0 && (
              <div className="customization-section">
                <h4 className="customization-title">Choose Color</h4>
                <div className="color-options">
                  {product.ProductColors.map((color) => (
                    <button
                      key={color.ColourId}
                      className={`color-option ${selectedColorId === color.ColourId ? 'selected' : ''}`}
                      onClick={() => chooseColor(color)}
                      style={{ backgroundColor: color.ColourHex }}
                      title={color.ColourName}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Size Selection */}
            {product.IsSizeSelection && product.ProductSizes && product.ProductSizes.length > 0 && (
              <div className="customization-section">
                <h4 className="customization-title">Choose Size</h4>
                <div className="size-options">
                  {product.ProductSizes.map((size) => (
                    <button
                      key={size.SizeId}
                      className={`size-option ${selectedSizeId === size.SizeId ? 'selected' : ''}`}
                      onClick={() => chooseSize(size)}
                    >
                      {size.SizeName}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Type Selection (Jacket/Hoodie/Sweatshirt) */}
            {product.Subcategory?.IsTypeNeeded && (
              <div className="customization-section">
                <h4 className="customization-title">Choose Type</h4>
                <div className="type-options">
                  <button
                    className={`type-option ${selectedZipType === 'Jacket With Zipper' ? 'selected' : ''}`}
                    onClick={() => chooseZip('Jacket With Zipper')}
                  >
                    Jacket With Zipper + AED {product.Subcategory.ZipPrice || 0}
                  </button>
                  <button
                    className={`type-option ${selectedZipType === 'Hoodie' ? 'selected' : ''}`}
                    onClick={() => chooseZip('Hoodie')}
                  >
                    Hoodie + AED {product.Subcategory.HoodiePrice || 0}
                  </button>
                  <button
                    className={`type-option ${selectedZipType === 'Sweatshirt' ? 'selected' : ''}`}
                    onClick={() => chooseZip('Sweatshirt')}
                  >
                    Sweatshirt + AED {product.Subcategory.SweatshirtPrice || 0}
                  </button>
                </div>
              </div>
            )}

            {/* Text and Photo Customization Options */}
            {(product.IsPhotoNeed || product.IsTextNeed) && (
              <div className="customization-section">
                <h4 className="customization-title">Customization Options</h4>

                <div className="customization-options">
                  {product.ProductCustomizations?.map((customization, index) => {
                    // Create a unique key for each checkbox
                    const uniqueId = `customization-${customization.CustomizationId}-${index}`;

                    return (
                      <div key={uniqueId} className="customization-option">
                        <div className="customization-checkbox">
                          <input
                            type="checkbox"
                            id={uniqueId}
                            name={uniqueId}
                            checked={customization.IsSelected || false}
                            onChange={(e) => {
                              const updatedCustomizations = product.ProductCustomizations.map(
                                (c, i) =>
                                  i === index
                                    ? { ...c, IsSelected: e.target.checked }
                                    : c
                              );

                              // Assuming you have setProduct to update the product state
                              setProduct({
                                ...product,
                                ProductCustomizations: updatedCustomizations,
                              });
                            }}
                          />
                          <label htmlFor={uniqueId}>
                            {customization.Customization} (+ AED {customization.Price})
                          </label>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <button
                  className="btn btn-secondary finish-customization-btn"
                  onClick={() => setIsCustomizeModalOpen(true)}
                >
                  Finish Customization
                </button>
              </div>
            )}

            {/* Quantity Selection */}
            <div className="quantity-section">
              <h4 className="customization-title">Quantity</h4>
              <div className="quantity-controls">
                <button className="quantity-btn" onClick={minQty}>-</button>
                <span className="quantity-display">{quantity}</span>
                <button className="quantity-btn" onClick={addQty}>+</button>
              </div>
            </div>

            <div className="details-actions">
              <button className="btn btn-secondary buy-now-btn"
                onClick={() => addToCart(product, quantity)}>
                <FaShoppingCart className="btn-icon" />
                Add To Cart
              </button>
            </div>

            <div className="details-meta">
              <div className="meta-item">
                <span className="meta-label">Category </span>
                <span className="meta-value"> {categoryLabel}</span>
              </div>
              <div className="meta-item">
                <span className="meta-label">Rating </span>
                <span className="meta-value"> {product.AvgRating || '—'}</span>
              </div>
              <div className="meta-item">
                <span className="meta-label">Stock </span>
                <span className="meta-value"> {product.Stock || 'In Stock'}</span>
              </div>
            </div>

            <div className="details-description">
              <h3>Description</h3>
              <p>
                {product.Description || product.Name || `This is a detailed description for ${decodedName}. It highlights all the amazing features and benefits of this product.`}
              </p>
            </div>
          </div>
        </div>

        {relatedProducts && relatedProducts.length > 0 && (
          <section className="related-products-section section">
            <h2 className="section-title"></h2>
            <ProductSlider
              products={relatedProducts}
              title="Customers Also Viewed"
              subtitle="Related to this item"
            />
          </section>
        )}
      </div>

      {isLightboxOpen && (
        <div className="lightbox-overlay" onClick={closeLightbox} role="dialog" aria-modal="true">
          <button className="lightbox-close" onClick={closeLightbox} aria-label="Close">×</button>
          {images.length > 1 && (
            <button className="lightbox-nav prev" onClick={showPrev} aria-label="Previous">‹</button>
          )}
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <img className="lightbox-image" src={images[lightboxIndex]} alt={decodedName} />
          </div>
          {images.length > 1 && (
            <button className="lightbox-nav next" onClick={showNext} aria-label="Next">›</button>
          )}
        </div>
      )}

      {/* Customization Modal */}
      {isCustomizeModalOpen && (
        <div className="modal-overlay" onClick={() => setIsCustomizeModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="modal-header">
              <h3>Customize Options</h3>
              <button
                className="modal-close"
                onClick={() => setIsCustomizeModalOpen(false)}
              >
                <FaTimes />
              </button>
            </div>

            {/* Body */}
            <div className="modal-body">
              {selectedCustomizations.map((customization, index) => {
                const uniqueId = customization.CustomizationId ?? index;

                return (
                  <div
                    key={uniqueId}
                    className="customization-input"
                  >
                    {/* TEXT TYPE */}
                    {customization.Type === "Text" && (
                      <>
                        <label>{customization.Customization}</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Enter text..."
                          value={customization.TextValue ?? ""}
                          onChange={(e) =>
                            setSelectedCustomizations((prev) =>
                              prev.map((c, i) =>
                                (c.CustomizationId ?? i) === uniqueId
                                  ? { ...c, TextValue: e.target.value }
                                  : c
                              )
                            )
                          }
                        />
                      </>
                    )}

                    {/* PHOTO TYPE */}
                    {customization.Type === "Photo" && (
                      <>
                        <label>Photo on {customization.Customization}</label>
                        <input
                          type="file"
                          accept="image/*"
                          className="form-control"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            const reader = new FileReader();
                            reader.onload = (ev) => {
                              setSelectedCustomizations((prev) =>
                                prev.map((c, i) =>
                                  (c.CustomizationId ?? i) === uniqueId
                                    ? { ...c, PhotoValue: ev.target.result }
                                    : c
                                )
                              );
                            };
                            reader.readAsDataURL(file);
                          }}
                        />
                        {customization.PhotoValue && (
                          <img
                            src={customization.PhotoValue}
                            alt="Preview"
                            className="customization-preview"
                          />
                        )}
                      </>
                    )}

                    {/* COLOR TYPE */}
                    {customization.Type === "Color" && (
                      <>
                        <label>{customization.Customization}</label>
                        <input
                          type="color"
                          className="form-control color-input"
                          value={customization.ColorValue ?? "#000000"}
                          onChange={(e) =>
                            setSelectedCustomizations((prev) =>
                              prev.map((c, i) =>
                                (c.CustomizationId ?? i) === uniqueId
                                  ? { ...c, ColorValue: e.target.value }
                                  : c
                              )
                            )
                          }
                        />
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="modal-footer">
              <button
                className="btn btn-primary"
                onClick={() => setIsCustomizeModalOpen(false)}
              >
                Save Customizations
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast
        visible={showAddToCartToast}
        message="Successfully added to cart!"
        onClose={() => setShowAddToCartToast(false)}
        actionLabel="View cart"
        actionTo="/cart"
      />
    </div>
  );
};

export default ProductDetails;
